import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../styles/ag-grid-custom.css';
import { FaArrowLeft, FaChartBar, FaCog, FaCopy, FaExternalLinkAlt, FaUsers, FaTrophy, FaEye, FaInfoCircle, FaList, FaPlane, FaRedo, FaUser, FaUpload, FaTrash } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && <div className="p-6">{children}</div>}
    </div>
  );
}

const SeriesDetailsMUI: React.FC = () => {
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [series, setSeries] = useState<any>(null);
  const [playerScores, setPlayerScores] = useState<any[]>([]);
  const [contests, setContests] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<number | null>(null);
  const [selectedPlayerScoreTeamFilter, setSelectedPlayerScoreTeamFilter] = useState<number | null>(null);
  const [contestDetailsModalOpen, setContestDetailsModalOpen] = useState(false);
  const [selectedContest, setSelectedContest] = useState<any>(null);
  const [contestMembers, setContestMembers] = useState<any[]>([]);
  const [contestLeaderboard, setContestLeaderboard] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [contestDetailsTab, setContestDetailsTab] = useState(0);
  const [fetchingTeams, setFetchingTeams] = useState(false);
  const [fetchingMatches, setFetchingMatches] = useState(false);
  const [fetchingSquads, setFetchingSquads] = useState(false);
  const [fetchingImages, setFetchingImages] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [playerScoreDetailsModalOpen, setPlayerScoreDetailsModalOpen] = useState(false);
  const [selectedPlayerScore, setSelectedPlayerScore] = useState<any>(null);
  const [loadingPlayerScores, setLoadingPlayerScores] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishAction, setPublishAction] = useState<'publish' | 'unpublish'>('publish');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  const [seriesImageKey, setSeriesImageKey] = useState(Date.now());
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Feature Template State
  const [featureTemplates, setFeatureTemplates] = useState<{ _id?: string; id?: string; name: string }[]>([]);
  const [selectedFeatureTemplateId, setSelectedFeatureTemplateId] = useState('');
  const [seriesFeatures, setSeriesFeatures] = useState<any>({ featureCategories: [], tierPricing: {} });
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [loadingTemplatesList, setLoadingTemplatesList] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);

  // Rules Config Template State
  const [rulesConfigTemplates, setRulesConfigTemplates] = useState<{ _id?: string; id?: string; name: string }[]>([]);
  const [selectedRulesConfigTemplateId, setSelectedRulesConfigTemplateId] = useState('');
  const [seriesRulesConfig, setSeriesRulesConfig] = useState<any>({ sections: [] });
  const [loadingRulesConfig, setLoadingRulesConfig] = useState(false);
  const [loadingRulesConfigTemplatesList, setLoadingRulesConfigTemplatesList] = useState(false);
  const [savingRulesConfig, setSavingRulesConfig] = useState(false);

  // Scoring Rules Template State
  const [scoringRulesTemplates, setScoringRulesTemplates] = useState<{ _id?: string; id?: string; name: string }[]>([]);
  const [selectedScoringRulesTemplateId, setSelectedScoringRulesTemplateId] = useState('');
  const [seriesScoringRules, setSeriesScoringRules] = useState<any>({ sections: [] });
  const [loadingScoringRules, setLoadingScoringRules] = useState(false);
  const [loadingScoringRulesTemplatesList, setLoadingScoringRulesTemplatesList] = useState(false);
  const [savingScoringRules, setSavingScoringRules] = useState(false);

  // Series Edit State
  const [editAlternateName, setEditAlternateName] = useState('');
  const [editShortName, setEditShortName] = useState('');
  const [editTrending, setEditTrending] = useState(false);
  const [editActive, setEditActive] = useState(false);
  const [editCountry, setEditCountry] = useState('');
  const [editIsFree, setEditIsFree] = useState(false);
  const [savingSeriesInfo, setSavingSeriesInfo] = useState(false);
  const [savingTrending, setSavingTrending] = useState(false);
  const [savingActive, setSavingActive] = useState(false);
  const [savingIsFree, setSavingIsFree] = useState(false);
  const [showEditSeriesDialog, setShowEditSeriesDialog] = useState(false);

  // Teams, Squads, Matches State (fetched from separate endpoints)
  const [teams, setTeams] = useState<any[]>([]);
  const [squads, setSquads] = useState<any[]>([]);
  const [matches, setMatches] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingSquads, setLoadingSquads] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(false);

  useEffect(() => {
    if (seriesId) {
      loadSeriesDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  const loadSeriesDetails = async () => {
    try {
      setLoading(true);
      const seriesData = await apiService.getSeriesById(seriesId!);
      console.log('Series fetched by ID:', seriesData);
      setSeries(seriesData);

      // Initialize edit fields
      const seriesDataAny = seriesData as any;
      setEditAlternateName(seriesDataAny.alternateName || '');
      setEditShortName(seriesDataAny.shortName || '');
      setEditCountry(seriesDataAny.country || '');
      setEditTrending(seriesDataAny.trending || false);
      setEditActive(seriesDataAny.active !== undefined ? seriesDataAny.active : true);
      setEditIsFree(seriesDataAny.planConfig?.isFree || false);

      // Load planConfig if it exists in the series
      const seriesAny = seriesData as any;
      if (seriesAny.planConfig) {
        console.log('Loading existing planConfig:', seriesAny.planConfig);
        setSeriesFeatures({
          featureCategories: seriesAny.planConfig.featureCategories || [],
          tierPricing: seriesAny.planConfig.tierPricing || {}
        });
        if (seriesAny.planConfig.templateId) {
          setSelectedFeatureTemplateId(seriesAny.planConfig.templateId);
        }
      }
      // Load rulesConfig if it exists in the series (stored as array of sections)
      if (seriesAny.rulesConfig) {
        console.log('Loading existing rulesConfig:', seriesAny.rulesConfig);
        const rc = seriesAny.rulesConfig;
        setSeriesRulesConfig({
          sections: Array.isArray(rc) ? rc : (rc.sections || [])
        });
      }
      if (seriesAny.rulesConfigTemplateId) {
        setSelectedRulesConfigTemplateId(seriesAny.rulesConfigTemplateId);
      }
      // Load scoringConfig from the series (handle all possible key names and shapes)
      const scoringData = seriesAny.scoringConfig || seriesAny.scoringRulesConfig || seriesAny.scoringSystemConfig;
      if (scoringData) {
        console.log('Loading existing scoringConfig:', scoringData);
        setSeriesScoringRules({
          sections: Array.isArray(scoringData) ? scoringData : (scoringData.sections || scoringData.scoringConfig || scoringData.scoringSystemConfig || [])
        });
      }
      if (seriesAny.scoringConfigTemplateId) {
        setSelectedScoringRulesTemplateId(seriesAny.scoringConfigTemplateId);
      }
    } catch (err) {
      console.error('Error fetching series by ID:', err);
      setError('Failed to fetch series details');
    } finally {
      setLoading(false);
    }
  };

  // Load teams, squads, and matches from separate endpoints
  const loadTeams = async (force: boolean = false) => {
    if (!seriesId) return;
    try {
      setLoadingTeams(true);
      const teamsData = await apiService.getTeamsBySeries(seriesId, force);
      console.log('Teams loaded:', teamsData);
      setTeams(teamsData || []);
    } catch (err) {
      console.error('Error loading teams:', err);
      toast.error('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadSquads = async (force: boolean = false) => {
    if (!seriesId) return;
    try {
      setLoadingSquads(true);
      const squadsData = await apiService.getSquadsBySeries(seriesId, force);
      console.log('Squads loaded:', squadsData);
      setSquads(squadsData || []);
    } catch (err) {
      console.error('Error loading squads:', err);
      toast.error('Failed to load squads');
    } finally {
      setLoadingSquads(false);
    }
  };

  const loadMatches = async (force: boolean = false) => {
    if (!seriesId) return;
    try {
      setLoadingMatches(true);
      const matchesData = await apiService.getMatches(seriesId, force);
      console.log('Matches loaded:', matchesData);
      setMatches(matchesData || []);
    } catch (err) {
      console.error('Error loading matches:', err);
      toast.error('Failed to load matches');
    } finally {
      setLoadingMatches(false);
    }
  };

  // Load teams, squads, matches when seriesId changes
  useEffect(() => {
    if (seriesId) {
      loadTeams();
      loadSquads();
      loadMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  const handleSaveSeriesInfo = async () => {
    if (!seriesId) return;
    try {
      setSavingSeriesInfo(true);
      await apiService.updateSeries(seriesId, {
        alternateName: editAlternateName,
        shortName: editShortName,
        country: editCountry,
      } as any);
      // Update local series state
      setSeries((prev: any) => ({
        ...prev,
        alternateName: editAlternateName,
        shortName: editShortName,
        country: editCountry,
      }));
      toast.success('Series information updated successfully!');
    } catch (err) {
      console.error('Error updating series info:', err);
      toast.error('Failed to update series information');
    } finally {
      setSavingSeriesInfo(false);
    }
  };

  const handleToggleTrending = async () => {
    if (!seriesId) return;
    try {
      setSavingTrending(true);
      const newTrendingValue = !editTrending;
      await apiService.updateSeries(seriesId, {
        trending: newTrendingValue,
      } as any);
      setEditTrending(newTrendingValue);
      setSeries((prev: any) => ({
        ...prev,
        trending: newTrendingValue,
      }));
      toast.success(`Series ${newTrendingValue ? 'marked as trending' : 'removed from trending'}!`);
    } catch (err) {
      console.error('Error updating trending status:', err);
      toast.error('Failed to update trending status');
    } finally {
      setSavingTrending(false);
    }
  };

  const handleToggleActive = async () => {
    if (!seriesId) return;
    try {
      setSavingActive(true);
      const newActiveValue = !editActive;
      await apiService.updateSeries(seriesId, {
        active: newActiveValue,
      } as any);
      setEditActive(newActiveValue);
      setSeries((prev: any) => ({
        ...prev,
        active: newActiveValue,
      }));
      toast.success(`Series ${newActiveValue ? 'activated' : 'deactivated'}!`);
    } catch (err) {
      console.error('Error updating active status:', err);
      toast.error('Failed to update active status');
    } finally {
      setSavingActive(false);
    }
  };

  const handleToggleIsFree = async () => {
    if (!seriesId) return;
    try {
      setSavingIsFree(true);
      const newValue = !editIsFree;
      await apiService.updateSeries(seriesId, {
        planConfig: {
          ...((series as any)?.planConfig || {}),
          isFree: newValue,
        },
      } as any);
      setEditIsFree(newValue);
      setSeries((prev: any) => ({
        ...prev,
        planConfig: { ...(prev?.planConfig || {}), isFree: newValue },
      }));
      toast.success(`Series ${newValue ? 'marked as free (no plans)' : 'set to use plans'}!`);
    } catch (err) {
      console.error('Error updating isFree status:', err);
      toast.error('Failed to update free status');
    } finally {
      setSavingIsFree(false);
    }
  };

  const loadPlayerScores = async () => {
    if (!seriesId || playerScores.length > 0) return;
    try {
      setLoadingPlayerScores(true);
      const data = await apiService.getPlayerScores(seriesId);
      setPlayerScores(data || []);
    } catch (err) {
      console.error('Error fetching player scores:', err);
    } finally {
      setLoadingPlayerScores(false);
    }
  };

  const loadContests = async () => {
    const contestSeriesId = series?.seriesId || seriesId;
    if (!contestSeriesId || contests.length > 0) return;
    try {
      const data = await apiService.getContests(String(contestSeriesId));
      console.log('SeriesDetails - Contests data:', data);
      setContests(data || []);
    } catch (err) {
      console.error('Error fetching contests:', err);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !seriesId) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      await apiService.uploadSeriesImage(seriesId, file);
      toast.success('Series image uploaded successfully!');
      setSeriesImageKey(Date.now()); // Force image refresh
    } catch (err: any) {
      console.error('Error uploading image:', err);
      toast.error(`Failed to upload image: ${err.message}`);
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageDelete = async () => {
    if (!seriesId) return;

    if (!window.confirm('Are you sure you want to remove the series image?')) {
      return;
    }

    try {
      setDeletingImage(true);
      await apiService.deleteSeriesImage(seriesId);
      toast.success('Series image removed successfully!');
      setSeriesImageKey(Date.now()); // Force image refresh
    } catch (err: any) {
      console.error('Error deleting image:', err);
      toast.error(`Failed to remove image: ${err.message}`);
    } finally {
      setDeletingImage(false);
    }
  };

  // Load feature templates list
  const loadFeatureTemplates = async () => {
    try {
      setLoadingTemplatesList(true);
      console.log('Loading feature templates...');
      const templates = await apiService.getFeatureRulesTemplates();
      console.log('Feature templates loaded:', templates);
      setFeatureTemplates(templates || []);
    } catch (err) {
      console.error('Error loading feature templates:', err);
    } finally {
      setLoadingTemplatesList(false);
    }
  };

  // Load a specific template for the series
  const loadFeatureTemplate = async (templateId: string) => {
    if (!templateId) {
      setSeriesFeatures({ featureCategories: [], tierPricing: {} });
      setSelectedFeatureTemplateId('');
      return;
    }
    try {
      setLoadingFeatures(true);
      const template = await apiService.getFeatureRulesTemplate(templateId);
      console.log('Series template response:', template);
      if (template) {
        // Data is at root level (flat structure)
        setSeriesFeatures({
          featureCategories: template.featureCategories || [],
          tierPricing: template.tierPricing || {}
        });
        setSelectedFeatureTemplateId(templateId);
      }
    } catch (err: any) {
      console.error('Error loading template:', err);
      toast.error(`Failed to load template: ${err.message}`);
    } finally {
      setLoadingFeatures(false);
    }
  };

  // Update feature price in the series config
  const updateFeaturePrice = (categoryKey: string, featureKey: string, newPrice: number) => {
    setSeriesFeatures((prev: any) => ({
      ...prev,
      featureCategories: prev.featureCategories.map((c: any) =>
        c.key === categoryKey
          ? {
              ...c,
              features: c.features.map((f: any) =>
                f.key === featureKey ? { ...f, price: newPrice } : f
              )
            }
          : c
      )
    }));
  };

  // Save plan config to series
  const saveSeriesFeatures = async () => {
    if (!seriesId) return;
    try {
      setSavingFeatures(true);
      const planConfig = {
        tierPricing: seriesFeatures.tierPricing || {},
        featureCategories: seriesFeatures.featureCategories || [],
        templateId: selectedFeatureTemplateId || undefined
      };
      console.log('Saving planConfig to series:', planConfig);
      await apiService.updateSeries(seriesId, { planConfig } as any);
      toast.success('Plan config saved to series!');
    } catch (err: any) {
      console.error('Error saving plan config:', err);
      toast.error(`Failed to save plan config: ${err.message}`);
    } finally {
      setSavingFeatures(false);
    }
  };

  // Load rules config templates list
  const loadRulesConfigTemplates = async () => {
    try {
      setLoadingRulesConfigTemplatesList(true);
      console.log('Loading rules config templates...');
      const templates = await apiService.getContestRulesTemplates();
      console.log('Rules config templates loaded:', templates);
      setRulesConfigTemplates(templates || []);
    } catch (err) {
      console.error('Error loading rules config templates:', err);
    } finally {
      setLoadingRulesConfigTemplatesList(false);
    }
  };

  // Load a specific rules config template for the series
  const loadRulesConfigTemplate = async (templateId: string) => {
    if (!templateId) {
      setSeriesRulesConfig({ sections: [] });
      setSelectedRulesConfigTemplateId('');
      return;
    }
    try {
      setLoadingRulesConfig(true);
      const template = await apiService.getContestRulesTemplate(templateId);
      console.log('Rules config template response:', template);
      if (template) {
        const rc = template.rulesConfig || template.sections || [];
        setSeriesRulesConfig({
          sections: Array.isArray(rc) ? rc : (rc.sections || [])
        });
        setSelectedRulesConfigTemplateId(templateId);
      }
    } catch (err: any) {
      console.error('Error loading rules config template:', err);
      toast.error(`Failed to load rules config template: ${err.message}`);
    } finally {
      setLoadingRulesConfig(false);
    }
  };

  // Save rules config to series
  const saveSeriesRulesConfig = async () => {
    if (!seriesId) return;
    try {
      setSavingRulesConfig(true);
      // rulesConfig is stored as an array of sections directly on the series
      const rulesConfig = seriesRulesConfig.sections || [];
      const rulesConfigTemplateId = selectedRulesConfigTemplateId || undefined;
      console.log('Saving rulesConfig to series:', rulesConfig);
      await apiService.updateSeries(seriesId, { rulesConfig, rulesConfigTemplateId } as any);
      toast.success('Rules config saved to series!');
    } catch (err: any) {
      console.error('Error saving rules config:', err);
      toast.error(`Failed to save rules config: ${err.message}`);
    } finally {
      setSavingRulesConfig(false);
    }
  };

  // Load scoring rules templates list
  const loadScoringRulesTemplates = async () => {
    try {
      setLoadingScoringRulesTemplatesList(true);
      console.log('Loading scoring rules templates...');
      const templates = await apiService.getScoringRulesTemplates();
      console.log('Scoring rules templates loaded:', templates);
      setScoringRulesTemplates(templates || []);
    } catch (err) {
      console.error('Error loading scoring rules templates:', err);
    } finally {
      setLoadingScoringRulesTemplatesList(false);
    }
  };

  // Load a specific scoring rules template for the series
  const loadScoringRulesTemplate = async (templateId: string) => {
    if (!templateId) {
      setSeriesScoringRules({ sections: [] });
      setSelectedScoringRulesTemplateId('');
      return;
    }
    try {
      setLoadingScoringRules(true);
      const template = await apiService.getScoringRulesTemplate(templateId);
      console.log('Scoring rules template response:', template);
      if (template) {
        // Handle both array and object shapes
        const sc = template.scoringConfig || template.sections || template;
        setSeriesScoringRules({
          sections: Array.isArray(sc) ? sc : (sc.sections || [])
        });
        setSelectedScoringRulesTemplateId(templateId);
      }
    } catch (err: any) {
      console.error('Error loading scoring rules template:', err);
      toast.error(`Failed to load scoring rules template: ${err.message}`);
    } finally {
      setLoadingScoringRules(false);
    }
  };

  // Save scoring rules config to series
  const saveSeriesScoringRules = async () => {
    if (!seriesId) return;
    try {
      setSavingScoringRules(true);
      const scoringSystemConfig = seriesScoringRules.sections || [];
      const scoringConfigTemplateId = selectedScoringRulesTemplateId || undefined;
      console.log('Saving scoringSystemConfig to series:', scoringSystemConfig);
      await apiService.updateSeries(seriesId, { scoringSystemConfig, scoringConfigTemplateId } as any);
      toast.success('Scoring rules saved to series!');
    } catch (err: any) {
      console.error('Error saving scoring rules:', err);
      toast.error(`Failed to save scoring rules: ${err.message}`);
    } finally {
      setSavingScoringRules(false);
    }
  };

  // --- Plan Config: Clone, Delete, Clear ---
  const clonePlanConfigTemplate = async () => {
    if (!selectedFeatureTemplateId) {
      toast.error('Please select a template to clone');
      return;
    }
    const currentTemplate = featureTemplates.find(t => (t._id || t.id) === selectedFeatureTemplateId);
    const newName = prompt('Enter name for cloned template:', currentTemplate ? `${currentTemplate.name} (Copy)` : 'New Template');
    if (!newName?.trim()) return;
    try {
      await apiService.saveFeatureRulesTemplate(newName.trim(), {
        featureCategories: seriesFeatures.featureCategories || [],
        tierPricing: seriesFeatures.tierPricing || {}
      });
      toast.success('Plan config template cloned!');
      await loadFeatureTemplates();
    } catch (err: any) {
      toast.error(`Failed to clone template: ${err.message}`);
    }
  };

  const deletePlanConfigTemplate = async () => {
    if (!selectedFeatureTemplateId) {
      toast.error('Please select a template to delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await apiService.deleteFeatureRulesTemplate(selectedFeatureTemplateId);
      toast.success('Plan config template deleted!');
      setSelectedFeatureTemplateId('');
      setSeriesFeatures({ featureCategories: [], tierPricing: {} });
      await loadFeatureTemplates();
    } catch (err: any) {
      toast.error(`Failed to delete template: ${err.message}`);
    }
  };

  const clearPlanConfig = () => {
    setSeriesFeatures({ featureCategories: [], tierPricing: {} });
    setSelectedFeatureTemplateId('');
  };

  // --- Rules Config: Clone, Delete, Clear ---
  const cloneRulesConfigTemplate = async () => {
    if (!selectedRulesConfigTemplateId) {
      toast.error('Please select a template to clone');
      return;
    }
    const currentTemplate = rulesConfigTemplates.find(t => (t._id || t.id) === selectedRulesConfigTemplateId);
    const newName = prompt('Enter name for cloned template:', currentTemplate ? `${currentTemplate.name} (Copy)` : 'New Template');
    if (!newName?.trim()) return;
    try {
      await apiService.saveContestRulesTemplate(newName.trim(), { rulesConfig: seriesRulesConfig.sections || [] });
      toast.success('Rules config template cloned!');
      await loadRulesConfigTemplates();
    } catch (err: any) {
      toast.error(`Failed to clone template: ${err.message}`);
    }
  };

  const deleteRulesConfigTemplate = async () => {
    if (!selectedRulesConfigTemplateId) {
      toast.error('Please select a template to delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await apiService.deleteContestRulesTemplate(selectedRulesConfigTemplateId);
      toast.success('Rules config template deleted!');
      setSelectedRulesConfigTemplateId('');
      setSeriesRulesConfig({ sections: [] });
      await loadRulesConfigTemplates();
    } catch (err: any) {
      toast.error(`Failed to delete template: ${err.message}`);
    }
  };

  const clearRulesConfig = () => {
    setSeriesRulesConfig({ sections: [] });
    setSelectedRulesConfigTemplateId('');
  };

  // --- Scoring Rules: Clone, Delete, Clear ---
  const cloneScoringRulesTemplate = async () => {
    if (!selectedScoringRulesTemplateId) {
      toast.error('Please select a template to clone');
      return;
    }
    const currentTemplate = scoringRulesTemplates.find(t => (t._id || t.id) === selectedScoringRulesTemplateId);
    const newName = prompt('Enter name for cloned template:', currentTemplate ? `${currentTemplate.name} (Copy)` : 'New Template');
    if (!newName?.trim()) return;
    try {
      await apiService.saveScoringRulesTemplate(newName.trim(), { scoringConfig: seriesScoringRules.sections || [] });
      toast.success('Scoring rules template cloned!');
      await loadScoringRulesTemplates();
    } catch (err: any) {
      toast.error(`Failed to clone template: ${err.message}`);
    }
  };

  const deleteScoringRulesTemplate = async () => {
    if (!selectedScoringRulesTemplateId) {
      toast.error('Please select a template to delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await apiService.deleteScoringRulesTemplate(selectedScoringRulesTemplateId);
      toast.success('Scoring rules template deleted!');
      setSelectedScoringRulesTemplateId('');
      setSeriesScoringRules({ sections: [] });
      await loadScoringRulesTemplates();
    } catch (err: any) {
      toast.error(`Failed to delete template: ${err.message}`);
    }
  };

  const clearScoringRules = () => {
    setSeriesScoringRules({ sections: [] });
    setSelectedScoringRulesTemplateId('');
  };

  // Load templates on mount
  useEffect(() => {
    loadFeatureTemplates();
    loadRulesConfigTemplates();
    loadScoringRulesTemplates();
  }, []);

  const handleFetchTeams = async () => {
    if (!seriesId) return;
    try {
      setFetchingTeams(true);
      await apiService.fetchTeamsForSeries(seriesId);
      toast.success('Teams fetched successfully!');
      await loadTeams(true);
    } catch (err: any) {
      console.error('Error fetching teams:', err);
      toast.error(`Failed to fetch teams: ${err.message}`);
    } finally {
      setFetchingTeams(false);
    }
  };

  const handleFetchMatches = async () => {
    if (!seriesId) return;
    try {
      setFetchingMatches(true);
      await apiService.fetchMatchesForSeries(seriesId);
      toast.success('Matches fetched successfully!');
      await loadMatches(true);
    } catch (err: any) {
      console.error('Error fetching matches:', err);
      toast.error(`Failed to fetch matches: ${err.message}`);
    } finally {
      setFetchingMatches(false);
    }
  };

  const handleFetchSquads = async () => {
    if (!seriesId) return;
    try {
      setFetchingSquads(true);
      await apiService.fetchSquadsForSeries(seriesId);
      toast.success('Squads fetched successfully!');
      await loadSquads(true);
    } catch (err: any) {
      console.error('Error fetching squads:', err);
      toast.error(`Failed to fetch squads: ${err.message}`);
    } finally {
      setFetchingSquads(false);
    }
  };

  const handleFetchImages = async () => {
    if (!seriesId) return;
    const squadIds = teams.map((team: any) => team.squadId).filter(Boolean);
    if (squadIds.length === 0) {
      toast.error('No squad IDs found in teams');
      return;
    }
    try {
      setFetchingImages(true);
      await apiService.fetchSquadImages(seriesId, squadIds);
      toast.success('Player images fetched successfully!');
      await loadSquads();
    } catch (err: any) {
      console.error('Error fetching player images:', err);
      toast.error(`Failed to fetch player images: ${err.message}`);
    } finally {
      setFetchingImages(false);
    }
  };

  const handlePublishToggle = async () => {
    if (!seriesId || !series) return;

    // Check requirements before publishing
    if (!series.publish) {
      const missingItems: string[] = [];

      if (!series.alternateName || series.alternateName.trim() === '') {
        missingItems.push('Alternate Name');
      }
      if (!series.shortName || series.shortName.trim() === '') {
        missingItems.push('Short Name');
      }

      // Country is required for league series (used to determine overseas players)
      const seriesAny = series as any;
      if (seriesAny.type === 'league' && (!seriesAny.country || seriesAny.country.trim() === '')) {
        missingItems.push('Host Country');
      }

      // Check if planConfig is empty (check both local state and series data)
      const seriesAnyCheck = series as any;
      const hasPlanConfig = (seriesFeatures.featureCategories && seriesFeatures.featureCategories.length > 0)
        || (seriesAnyCheck.planConfig?.featureCategories?.length > 0);
      if (!hasPlanConfig) {
        missingItems.push('Plan Configuration');
      }

      // Check if rulesConfig is empty
      const hasRulesConfig = (seriesRulesConfig.sections && seriesRulesConfig.sections.length > 0)
        || (Array.isArray(seriesAnyCheck.rulesConfig) && seriesAnyCheck.rulesConfig.length > 0);
      if (!hasRulesConfig) {
        missingItems.push('Rules Configuration');
      }

      // Check if scoringConfig is empty
      const hasScoringConfig = (seriesScoringRules.sections && seriesScoringRules.sections.length > 0)
        || (Array.isArray(seriesAnyCheck.scoringConfig) && seriesAnyCheck.scoringConfig.length > 0)
        || (Array.isArray(seriesAnyCheck.scoringRulesConfig?.sections) && seriesAnyCheck.scoringRulesConfig.sections.length > 0)
        || (Array.isArray(seriesAnyCheck.scoringSystemConfig) && seriesAnyCheck.scoringSystemConfig.length > 0);
      if (!hasScoringConfig) {
        missingItems.push('Scoring Configuration');
      }

      if (missingItems.length > 0) {
        const missingText = missingItems.join(', ');
        toast.error(`Cannot publish: Missing ${missingText}. Please fill in all required fields first.`, {
          autoClose: 5000,
        });
        return;
      }
    }

    // Show confirmation dialog for both publish and unpublish
    if (series.publish) {
      setPublishAction('unpublish');
    } else {
      setPublishAction('publish');
    }
    setShowPublishDialog(true);
  };

  const confirmPublishToggle = async () => {
    if (!series) return;

    try {
      setPublishing(true);
      setShowPublishDialog(false);

      const seriesIdStr = String(series.seriesId || series.id);

      if (series.publish) {
        // Currently published, so unpublish
        await apiService.updateSeries(seriesIdStr, { publish: false } as any);
        setSeries({ ...series, publish: false });
        toast.success('Series unpublished successfully!');
      } else {
        // Currently unpublished, so publish (also set active to true)
        await apiService.updateSeries(seriesIdStr, { publish: true, active: true } as any);
        setSeries({ ...series, publish: true, active: true });
        setEditActive(true);
        toast.success('Series published successfully!');
      }
    } catch (err: any) {
      console.error('Error updating publish status:', err);
      toast.error(`Failed to update publish status: ${err.message}`);
    } finally {
      setPublishing(false);
    }
  };


  const openContestDetails = async (contest: any) => {
    setSelectedContest(contest);
    setContestDetailsModalOpen(true);
    setContestDetailsTab(0);
    setContestMembers([]);
    setContestLeaderboard([]);

    // Load members and leaderboard in background
    const contestId = contest.contestId || contest.id;

    // Load members
    setLoadingMembers(true);
    try {
      const membersData = await apiService.getContestMembers(contestId);
      setContestMembers(membersData || []);
    } catch (err) {
      console.error('Error fetching contest members:', err);
      setContestMembers([]);
    } finally {
      setLoadingMembers(false);
    }

    // Load leaderboard
    setLoadingLeaderboard(true);
    try {
      const leaderboardData = await apiService.getContestLeaderboard(contestId);
      setContestLeaderboard(leaderboardData || []);
    } catch (err) {
      console.error('Error fetching contest leaderboard:', err);
      setContestLeaderboard([]);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const getContestStatusColor = (status: string) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('active') || statusLower.includes('live')) return 'bg-green-100 text-green-800 border-green-300';
    if (statusLower.includes('upcoming') || statusLower.includes('scheduled')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (statusLower.includes('completed') || statusLower.includes('finished')) return 'bg-gray-100 text-gray-800 border-gray-300';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  // Generate dynamic columns from first item in contests array
  const contestsColumnDefs: ColDef[] = useMemo(() => {
    if (contests.length === 0) return [];

    const firstItem = contests[0];
    const keys = Object.keys(firstItem);

    console.log('SeriesDetails - Available fields in contest data:', keys);

    // Fields to exclude from grid
    const excludedFields = ['rules', 'scoringSystem', 'scoring_system', 'owner', 'ownerInfo', 'owner_info', 'seriesName', 'series_name'];
    const idFieldsToExclude = ['seriesId', 'series_id', 'ownerId', 'owner_id', 'userId', 'user_id', 'id'];

    const dataColumns = keys
      .filter((key) => {
        const keyLower = key.toLowerCase();
        if (excludedFields.some(excluded => keyLower === excluded.toLowerCase())) return false;
        if (idFieldsToExclude.some(excluded => keyLower === excluded.toLowerCase())) return false;
        return true;
      })
      .map((key) => {
        const value = firstItem[key];
        const isDate = key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key.toLowerCase().includes('createdat') || key.toLowerCase().includes('updatedat');
        const isBoolean = typeof value === 'boolean';
        const isNumber = typeof value === 'number' && !key.toLowerCase().includes('id');

        // Format header name: convert camelCase/snake_case to Title Case
        const headerName = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/^\w/, (c) => c.toUpperCase())
          .trim();

        const columnDef: ColDef = {
          field: key,
          headerName: headerName,
          filter: isNumber ? 'agNumberColumnFilter' : isDate ? 'agDateColumnFilter' : 'agTextColumnFilter',
          sortable: true,
          resizable: true,
          minWidth: 150,
          flex: 1,
        };

        // Add custom rendering for specific field types
        if (isBoolean) {
          columnDef.minWidth = 100;
          columnDef.flex = 0.5;
          columnDef.cellRenderer = (params: ICellRendererParams) => (
            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
              params.value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {params.value ? 'Yes' : 'No'}
            </span>
          );
        } else if (key.toLowerCase().includes('status')) {
          columnDef.minWidth = 140;
          columnDef.flex = 0.8;
          columnDef.cellRenderer = (params: ICellRendererParams) => {
            const statusColor = getContestStatusColor(params.value || '');
            return (
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${statusColor}`}>
                {params.value || 'N/A'}
              </span>
            );
          };
        } else if (isDate) {
          columnDef.minWidth = 200;
          columnDef.flex = 1.2;
          columnDef.valueFormatter = (params) => {
            if (!params.value) return 'N/A';
            try {
              return new Date(params.value).toLocaleString();
            } catch {
              return params.value;
            }
          };
        } else if (key.toLowerCase().includes('id')) {
          columnDef.minWidth = 250;
          columnDef.flex = 1.5;
          columnDef.cellRenderer = (params: ICellRendererParams) => (
            <div className="font-mono text-xs">{params.value || 'N/A'}</div>
          );
        } else if (typeof value === 'object' && value !== null) {
          // Handle object and array fields
          columnDef.minWidth = 200;
          columnDef.flex = 1.2;
          columnDef.cellRenderer = (params: ICellRendererParams) => {
            if (!params.value) return <span className="text-gray-400 italic">—</span>;

            if (Array.isArray(params.value)) {
              if (params.value.length === 0) {
                return <span className="text-gray-400 italic text-xs">Empty</span>;
              }
              return (
                <div className="text-xs text-gray-700">
                  {params.value.length} item{params.value.length !== 1 ? 's' : ''}
                </div>
              );
            }

            // For objects, show count of properties
            const keys = Object.keys(params.value);
            return (
              <div className="text-xs text-gray-700">
                {keys.length} field{keys.length !== 1 ? 's' : ''}
              </div>
            );
          };
        }

        return columnDef;
      });

    // Add Actions column
    const actionsColumn: ColDef = {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      pinned: 'right',
      sortable: false,
      filter: false,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams) => {
        const contest = params.data;

        return (
          <div className="flex gap-2">
            <button
              onClick={() => openContestDetails(contest)}
              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
            >
              <FaEye className="mr-1.5" />
              View Details
            </button>
          </div>
        );
      },
    };

    return [...dataColumns, actionsColumn];
  }, [contests]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 5) {
      // Player Scores tab - reset team filter
      setSelectedPlayerScoreTeamFilter(null);
      loadPlayerScores();
    }
    if (newValue === 4) {
      // Contests tab
      loadContests();
    }
    if (newValue === 2) {
      // Squads tab - reset team filter
      setSelectedTeamFilter(null);
    }
  };

  // Flatten player scores data
  const flattenedPlayerScores = useMemo(() => {
    const filteredScores = playerScores.filter((player) =>
      selectedPlayerScoreTeamFilter === null || player.teamId === selectedPlayerScoreTeamFilter
    );

    return filteredScores.map((player) => {
      // Handle missing matches array
      if (!player.matches || !Array.isArray(player.matches) || player.matches.length === 0) {
        return {
          ...player,
          originalPlayer: player,
          matches: [],
          matchesPlayed: 0,
          runsScored: 0,
          ballsFaced: 0,
          fours: 0,
          sixes: 0,
          wickets: 0,
          overs: 0,
          runsConceded: 0,
          catches: 0,
          stumps: 0,
          runouts: 0,
          mom: 0,
          battingAvg: '0.00',
          bowlingAvg: '-',
          economy: '-',
        };
      }

      const totalStats = player.matches.reduce(
        (acc: any, match: any) => {
          const breakdown = match.scoresBreakdown || {};
          return {
            matchesPlayed: acc.matchesPlayed + 1,
            runsScored: acc.runsScored + (breakdown.runsScored || 0),
            ballsFaced: acc.ballsFaced + (breakdown.ballsFaced || 0),
            fours: acc.fours + (breakdown.fours || 0),
            sixes: acc.sixes + (breakdown.sixes || 0),
            wickets: acc.wickets + (breakdown.wickets || 0),
            overs: acc.overs + (breakdown.overs || 0),
            runsConceded: acc.runsConceded + (breakdown.runsConceded || 0),
            catches: acc.catches + (breakdown.catch || 0),
            stumps: acc.stumps + (breakdown.stump || 0),
            runouts: acc.runouts + (breakdown.runouts || 0),
            mom: acc.mom + (breakdown.mom || 0),
          };
        },
        {
          matchesPlayed: 0,
          runsScored: 0,
          ballsFaced: 0,
          fours: 0,
          sixes: 0,
          wickets: 0,
          overs: 0,
          runsConceded: 0,
          catches: 0,
          stumps: 0,
          runouts: 0,
          mom: 0,
        }
      );

      const battingAvg = totalStats.ballsFaced > 0 ? (totalStats.runsScored / totalStats.ballsFaced) * 100 : 0;
      const bowlingAvg = totalStats.wickets > 0 ? totalStats.runsConceded / totalStats.wickets : 0;
      const economy = totalStats.overs > 0 ? totalStats.runsConceded / totalStats.overs : 0;

      return {
        ...player,
        matches: player.matches, // Preserve original matches array for detail view
        ...totalStats,
        battingAvg: battingAvg.toFixed(2),
        bowlingAvg: bowlingAvg > 0 ? bowlingAvg.toFixed(2) : '-',
        economy: economy > 0 ? economy.toFixed(2) : '-',
      };
    });
  }, [playerScores, selectedPlayerScoreTeamFilter]);

  const handlePlayerScoreRowClick = (event: any) => {
    if (event.data) {
      setSelectedPlayerScore(event.data);
      setPlayerScoreDetailsModalOpen(true);
    }
  };

  const playerScoresColumns: ColDef[] = useMemo(
    () => {
      // Get team name by ID
      const getTeamName = (teamId: number) => {
        const team = teams?.find((t: any) => t.teamId === teamId);
        return team?.teamName || `Team ${teamId}`;
      };

      return [
        {
          field: 'playerId',
          headerName: 'Player ID',
          width: 120,
          filter: 'agNumberColumnFilter',
          pinned: 'left',
        },
        {
          field: 'playerName',
          headerName: 'Player',
          flex: 1,
          minWidth: 200,
          filter: 'agTextColumnFilter',
          pinned: 'left',
        },
        {
          field: 'teamId',
          headerName: 'Team',
          width: 150,
          filter: 'agTextColumnFilter',
          valueGetter: (params: any) => getTeamName(params.data?.teamId),
        },
        // Batting Stats
        { field: 'matchesPlayed', headerName: 'Matches', width: 90, filter: 'agNumberColumnFilter', headerClass: 'ag-header-batting' },
        { field: 'runsScored', headerName: 'Runs', width: 100, filter: 'agNumberColumnFilter', cellStyle: { fontWeight: 600 }, headerClass: 'ag-header-batting' },
        { field: 'ballsFaced', headerName: 'Balls', width: 100, filter: 'agNumberColumnFilter', headerClass: 'ag-header-batting' },
        { field: 'battingAvg', headerName: 'Strike Rate', width: 110, filter: 'agNumberColumnFilter', headerClass: 'ag-header-batting' },
        { field: 'fours', headerName: '4s', width: 80, filter: 'agNumberColumnFilter', headerClass: 'ag-header-batting' },
        { field: 'sixes', headerName: '6s', width: 80, filter: 'agNumberColumnFilter', headerClass: 'ag-header-batting' },
        // Bowling Stats
        { field: 'wickets', headerName: 'Wickets', width: 100, filter: 'agNumberColumnFilter', cellStyle: { fontWeight: 600 }, headerClass: 'ag-header-bowling' },
        { field: 'overs', headerName: 'Overs', width: 100, filter: 'agNumberColumnFilter', headerClass: 'ag-header-bowling' },
        { field: 'runsConceded', headerName: 'Runs Conc.', width: 110, filter: 'agNumberColumnFilter', headerClass: 'ag-header-bowling' },
        { field: 'economy', headerName: 'Economy', width: 100, filter: 'agNumberColumnFilter', headerClass: 'ag-header-bowling' },
        // Fielding Stats
        { field: 'catches', headerName: 'Catches', width: 100, filter: 'agNumberColumnFilter', headerClass: 'ag-header-fielding' },
        { field: 'stumps', headerName: 'Stumps', width: 100, filter: 'agNumberColumnFilter', headerClass: 'ag-header-fielding' },
        { field: 'runouts', headerName: 'Runouts', width: 100, filter: 'agNumberColumnFilter', headerClass: 'ag-header-fielding' },
      ];
    },
    [series]
  );


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !series) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-800">{error || 'Series not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
        <div className="p-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard/series')}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-400/30 rounded-lg dark:rounded-xl transition-colors"
            >
              <FaArrowLeft className="h-5 w-5" />
            </button>

            {/* Series Image */}
            <div className="relative group">
              <img
                key={seriesImageKey}
                src={`https://cricclash-s3.s3.us-east-2.amazonaws.com/series/${series.seriesId || series.originalSeriesId || seriesId}.jpg?t=${seriesImageKey}`}
                alt={series.name}
                className="w-16 h-16 rounded-lg object-cover border-2 border-gray-200 dark:border-dark-300/40"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNiAxMCA1IDIxIj48L3BvbHlsaW5lPjwvc3ZnPg==';
                }}
              />
              {/* Image action buttons */}
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <label
                  className="p-1.5 bg-blue-600 text-white rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-md"
                  title="Upload image"
                >
                  <FaUpload className="h-3 w-3" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                </label>
                <button
                  onClick={handleImageDelete}
                  disabled={deletingImage}
                  className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-md disabled:opacity-50"
                  title="Remove image"
                >
                  <FaTrash className="h-3 w-3" />
                </button>
              </div>
              {/* Loading overlay */}
              {(uploadingImage || deletingImage) && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{series.name}</h1>
                {series.shortName && (
                  <span className="text-lg text-gray-500 dark:text-gray-400">({series.shortName})</span>
                )}
                <button
                  onClick={() => setShowEditSeriesDialog(true)}
                  className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-400/50 rounded-lg transition-colors"
                  title="Edit series information"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </div>
              {series.alternateName && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{series.alternateName}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold border bg-blue-100 dark:bg-primary-500/20 text-blue-800 dark:text-primary-400 border-blue-300 dark:border-primary-500/30">
                {matches?.[0]?.matchFormat || 'ODI'}
              </span>
              {series.trending && (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold border bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-400 border-purple-300 dark:border-purple-500/30">
                  Trending
                </span>
              )}
              {editIsFree && (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold border bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30">
                  Free
                </span>
              )}
              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${
                series.active ? 'bg-green-100 dark:bg-success-500/20 text-green-800 dark:text-success-500 border-green-300 dark:border-success-500/30' : 'bg-gray-100 dark:bg-dark-300/40 text-gray-800 dark:text-gray-400 border-gray-300 dark:border-dark-300/40'
              }`}>
                {series.active ? 'Active' : 'Inactive'}
              </span>
              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${
                series.publish ? 'bg-green-100 dark:bg-success-500/20 text-green-800 dark:text-success-500 border-green-300 dark:border-success-500/30' : 'bg-orange-100 dark:bg-warning-500/20 text-orange-800 dark:text-warning-500 border-orange-300 dark:border-warning-500/30'
              }`}>
                {series.publish ? 'Published' : 'Draft'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-300/40">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Actions</h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleFetchTeams}
                  disabled={fetchingTeams}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {fetchingTeams ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <FaUsers className="h-4 w-4 mr-2" />
                  )}
                  Fetch Teams
                </button>

                <button
                  onClick={handleFetchMatches}
                  disabled={fetchingMatches}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {fetchingMatches ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <FaTrophy className="h-4 w-4 mr-2" />
                  )}
                  Fetch Matches
                </button>

                <button
                  onClick={handleFetchSquads}
                  disabled={fetchingSquads}
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {fetchingSquads ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <FaList className="h-4 w-4 mr-2" />
                  )}
                  Fetch Squads
                </button>

                <div className="h-8 w-px bg-gray-300 dark:bg-dark-300/40"></div>

                <button
                  onClick={handlePublishToggle}
                  disabled={publishing}
                  className={`inline-flex items-center px-4 py-2 text-white rounded-md text-sm font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    series.publish
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                  title={series.publish ? 'Unpublish series' : 'Publish series'}
                >
                  {publishing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : series.publish ? (
                    <FiX className="h-4 w-4 mr-2" />
                  ) : (
                    <FaEye className="h-4 w-4 mr-2" />
                  )}
                  {series.publish ? 'Unpublish' : 'Publish'}
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 border-t border-gray-200 dark:border-dark-300/40">
            <nav className="flex space-x-4 mt-4">
              {[
                { label: 'Overview', index: 0 },
                { label: 'Teams', index: 1 },
                { label: 'Squads', index: 2 },
                { label: 'Matches', index: 3 },
                { label: 'Contests', index: 4 },
                { label: 'Player Scores', index: 5, icon: <FaChartBar className="h-4 w-4" /> },
                { label: 'Plan Config', index: 6, icon: <FaList className="h-4 w-4" /> },
                { label: 'Rules Config', index: 7, icon: <FaCog className="h-4 w-4" /> },
                { label: 'Scoring Rules', index: 8, icon: <FaChartBar className="h-4 w-4" /> },
              ].map((tab) => (
                <button
                  key={tab.index}
                  onClick={() => handleTabChange({} as any, tab.index)}
                  className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    tabValue === tab.index
                      ? 'bg-blue-600 dark:bg-primary-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-400/30'
                  }`}
                >
                  {tab.icon && tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-gray-50 dark:bg-transparent">
        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Series Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Series ID */}
              <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Series ID</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{series.seriesId}</div>
              </div>

              {/* Original Series ID */}
              <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Original Series ID</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{series.originalSeriesId !== undefined && series.originalSeriesId !== null ? series.originalSeriesId : 'N/A'}</div>
              </div>

              {/* Type */}
              <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Type</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{series.type || 'N/A'}</div>
              </div>

              {/* Teams */}
              <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Teams</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{teams?.length || 0}</div>
              </div>

              {/* Matches */}
              <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Matches</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{matches?.length || 0}</div>
              </div>

              {/* Published */}
              <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Published</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{series.publish ? 'Yes' : 'No'}</div>
              </div>

              {/* End Date */}
              <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">End Date</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {series.endDate ? new Date(series.endDate).toLocaleDateString() : 'N/A'}
                </div>
              </div>

              {/* Start Date - Full Width */}
              <div className="md:col-span-2 lg:col-span-3 bg-blue-50 dark:bg-primary-500/10 p-4 rounded-lg dark:rounded-xl border border-blue-200 dark:border-primary-500/30">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Start Date & Time</div>
                {(() => {
                  // Get start date from first match if available, otherwise from series
                  const startDate = matches?.[0]?.startDate || series.startDate;
                  const venueTimezone = matches?.[0]?.venueInfo?.timezone;

                  const formatDateTime = (dateString: string, timezone?: string) => {
                    if (!dateString) return 'N/A';
                    try {
                      const date = new Date(dateString);
                      const options: Intl.DateTimeFormatOptions = {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                        timeZoneName: 'short'
                      };
                      if (timezone) {
                        options.timeZone = timezone;
                      }
                      return date.toLocaleString('en-US', options);
                    } catch {
                      return dateString;
                    }
                  };

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">UTC</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatDateTime(startDate, 'UTC')}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Local</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatDateTime(startDate)}
                        </span>
                      </div>
                      {venueTimezone && (
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-blue-600 dark:text-primary-400 mb-1">Venue</span>
                          <span className="text-sm font-semibold text-blue-700 dark:text-primary-400">
                            {formatDateTime(startDate, venueTimezone)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          </TabPanel>

        {/* Teams Tab */}
        <TabPanel value={tabValue} index={1}>
          <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-300/40">
                <thead className="bg-gray-50 dark:bg-dark-400/30">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Team ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Team Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Squad ID</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-dark-300/40">
                  {teams && teams.length > 0 ? (
                    teams.map((team: any) => (
                      <tr key={team.teamId} className="hover:bg-gray-50 dark:hover:bg-dark-400/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{team.teamId}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">{team.teamName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
                            team.active ? 'bg-green-100 dark:bg-success-500/20 text-green-800 dark:text-success-500 border-green-300 dark:border-success-500/30' : 'bg-gray-100 dark:bg-dark-300/40 text-gray-800 dark:text-gray-400 border-gray-300 dark:border-dark-300/40'
                          }`}>
                            {team.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{team.squadId}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No teams found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabPanel>

        {/* Squads Tab */}
        <TabPanel value={tabValue} index={2}>
          {/* Team Filter */}
          {teams && teams.length > 0 && (
            <div className="mb-6 flex justify-between items-center">
              <div className="min-w-[250px] max-w-[350px]">
                <select
                  value={selectedTeamFilter === null ? '' : selectedTeamFilter}
                  onChange={(e) => setSelectedTeamFilter(e.target.value === '' ? null : Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent text-sm"
                >
                  <option value="">All Teams</option>
                  {teams.map((team: any) => (
                    <option key={team.teamId} value={team.teamId}>
                      {team.teamName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Player Count & Fetch Images */}
              <div className="flex items-center gap-3">
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-primary-500/20 text-blue-800 dark:text-primary-400 border border-blue-300 dark:border-primary-500/30">
                  Total Players: {
                    squads
                      ? squads.filter((player: any) =>
                          selectedTeamFilter === null || player.teamId === selectedTeamFilter
                        ).length
                      : 0
                  }
                </span>
                {selectedTeamFilter !== null && (
                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-sky-100 dark:bg-secondary-500/20 text-sky-800 dark:text-secondary-400 border border-sky-300 dark:border-secondary-500/30">
                    Viewing: {teams.find((t: any) => t.teamId === selectedTeamFilter)?.teamName || 'Unknown Team'}
                  </span>
                )}
                <button
                  onClick={handleFetchImages}
                  disabled={fetchingImages || !teams.some((t: any) => t.squadId)}
                  className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {fetchingImages ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <FaUser className="h-4 w-4 mr-2" />
                  )}
                  Fetch Player Images
                </button>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-300/40">
                <thead className="bg-gray-50 dark:bg-dark-400/30">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Image</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Player ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Player Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Team</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Country</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Batting Style</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Bowling Style</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-dark-300/40">
                  {squads && squads.length > 0 ? (
                    squads
                      .filter((player: any) =>
                        selectedTeamFilter === null || player.teamId === selectedTeamFilter
                      )
                      .map((player: any) => {
                        // Get role color based on player role
                        const getRoleColor = (role: string): string => {
                          const roleLower = (role || '').toLowerCase();

                          if (roleLower.includes('bats') || roleLower.includes('bat')) {
                            return 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-400 border-blue-300 dark:border-blue-500/30';
                          }
                          if (roleLower.includes('bowl')) {
                            return 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400 border-green-300 dark:border-green-500/30';
                          }
                          if (roleLower.includes('all') || roleLower.includes('rounder')) {
                            return 'bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-400 border-purple-300 dark:border-purple-500/30';
                          }
                          if (roleLower.includes('wicket') || roleLower.includes('wk') || roleLower.includes('keeper')) {
                            return 'bg-orange-100 dark:bg-orange-500/20 text-orange-800 dark:text-orange-400 border-orange-300 dark:border-orange-500/30';
                          }

                          return 'bg-gray-100 dark:bg-dark-300/40 text-gray-800 dark:text-gray-400 border-gray-300 dark:border-dark-300/40';
                        };

                        return (
                          <tr key={player.id} className="hover:bg-gray-50 dark:hover:bg-dark-400/50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="relative inline-block">
                                <img
                                  src={`https://cricclash-s3.s3.us-east-2.amazonaws.com/players/${player.playerId}.jpg`}
                                  alt={player.playerName}
                                  className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-dark-300/40"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.onerror = null;
                                    target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiI+PC9wYXRoPjxjaXJjbGUgY3g9IjEyIiBjeT0iNyIgcj0iNCI+PC9jaXJjbGU+PC9zdmc+';
                                  }}
                                />
                                {player.isOverseas && (
                                  <span
                                    className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 dark:bg-amber-500 shadow"
                                    title="Overseas player"
                                  >
                                    <FaPlane className="w-2.5 h-2.5 text-white -rotate-45" />
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{player.playerId}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">{player.playerName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{player.teamInfo?.teamName || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getRoleColor(player.playerRole)}`}>
                                {player.playerRole}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{player.intlTeam || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{player.battingStyle || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{player.bowlingStyle || '-'}</td>
                          </tr>
                        );
                      })
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                        No squad information found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabPanel>

        {/* Matches Tab */}
        <TabPanel value={tabValue} index={3}>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Match ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Teams</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Venue</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Start Time</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Result</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Scores Updated</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {matches && matches.length > 0 ? (
                    [...matches]
                      .sort((a: any, b: any) => {
                        // Sort by startDate in ascending order (earliest first)
                        const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
                        const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
                        return dateA - dateB;
                      })
                      .map((match: any) => {
                      // Get team names from localTeam and visitorTeam
                      const localTeamName = match.localTeam?.name || match.localTeam?.teamName || 'Team 1';
                      const visitorTeamName = match.visitorTeam?.name || match.visitorTeam?.teamName || 'Team 2';
                      const teamsDisplay = `${localTeamName} vs ${visitorTeamName}`;

                      // Get venue info from venueInfo
                      const venueGround = match.venueInfo?.ground || match.venue || '-';
                      const venueCity = match.venueInfo?.city ? `, ${match.venueInfo.city}` : '';
                      const venueDisplay = `${venueGround}${venueCity}`;

                      // Determine chip color based on status
                      const getStatusColor = (state: string): string => {
                        const stateLower = (state || '').toLowerCase();

                        if (stateLower.includes('finished') || stateLower.includes('completed')) return 'bg-green-100 text-green-800 border-green-300';
                        if (stateLower.includes('live') || stateLower.includes('in progress')) return 'bg-red-100 text-red-800 border-red-300';
                        if (stateLower.includes('upcoming') || stateLower.includes('scheduled')) return 'bg-blue-100 text-blue-800 border-blue-300';
                        if (stateLower.includes('cancelled') || stateLower.includes('abandoned') || stateLower.includes('aban')) return 'bg-gray-100 text-gray-800 border-gray-300';
                        if (stateLower.includes('delayed') || stateLower.includes('postponed')) return 'bg-orange-100 text-orange-800 border-orange-300';
                        if (stateLower.includes('rain') || stateLower.includes('interrupt')) return 'bg-sky-100 text-sky-800 border-sky-300';
                        if (stateLower.includes('break') || stateLower.includes('interval') || stateLower.includes('lunch') || stateLower.includes('tea')) return 'bg-purple-100 text-purple-800 border-purple-300';

                        return 'bg-blue-100 text-blue-800 border-blue-300';
                      };

                      // Format date time function
                      const formatDateTime = (dateString: string, timezone?: string) => {
                        if (!dateString) return '-';
                        try {
                          const date = new Date(dateString);
                          const options: Intl.DateTimeFormatOptions = {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            timeZoneName: 'short'
                          };
                          if (timezone) {
                            options.timeZone = timezone;
                          }
                          return date.toLocaleString('en-US', options);
                        } catch {
                          return dateString;
                        }
                      };

                      const venueTimezone = match.venueInfo?.timezone;

                      return (
                        <tr key={match.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{match.matchId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">{teamsDisplay}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{venueDisplay}</td>
                          <td className="px-4 py-3 text-xs text-gray-900">
                            <div className="space-y-1">
                              <div>
                                <span className="font-semibold text-gray-600">Local:</span> {formatDateTime(match.startDate)}
                              </div>
                              {venueTimezone && (
                                <div>
                                  <span className="font-semibold text-blue-600">Venue:</span> {formatDateTime(match.startDate, venueTimezone)}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(match.state)}`}>
                              {match.state || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 max-w-[250px]">
                            {match.status || '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              match.scoresUpdated ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-800 border-gray-300'
                            }`}>
                              {match.scoresUpdated ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                        No matches found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabPanel>

        {/* Contests Tab */}
        <TabPanel value={tabValue} index={4}>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="ag-theme-material" style={{ height: 'calc(100vh - 400px)', minHeight: '400px', width: '100%' }}>
              <AgGridReact
                rowData={contests}
                columnDefs={contestsColumnDefs}
                defaultColDef={{
                  sortable: true,
                  resizable: true,
                  filter: true,
                  minWidth: 120,
                  wrapText: true,
                  autoHeight: false
                }}
                pagination={true}
                paginationPageSize={25}
                paginationPageSizeSelector={[10, 25, 50, 100]}
                rowHeight={60}
                onFirstDataRendered={(params) => {
                  // Auto-size columns first
                  params.api.autoSizeAllColumns(false);

                  // Then add padding to prevent overlap
                  setTimeout(() => {
                    const allColumns = params.api.getColumns();
                    if (allColumns) {
                      const widthConfig: any = {};
                      allColumns.forEach((column: any) => {
                        const currentWidth = column.getActualWidth();
                        const colId = column.getColId();
                        widthConfig[colId] = currentWidth + 20;
                      });
                      params.api.setColumnWidths(Object.entries(widthConfig).map(([key, width]) => ({
                        key,
                        newWidth: width as number
                      })));
                    }
                  }, 100);
                }}
                animateRows={true}
                enableCellTextSelection={true}
              />
            </div>
          </div>
        </TabPanel>

        {/* Player Scores Tab */}
        <TabPanel value={tabValue} index={5}>
          {loadingPlayerScores ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="text-gray-600">Loading player scores...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Team Filter */}
              {teams && teams.length > 0 && playerScores.length > 0 && (
                <div className="mb-6 flex justify-between items-center">
                  <div className="min-w-[250px] max-w-[350px]">
                    <select
                      value={selectedPlayerScoreTeamFilter === null ? '' : selectedPlayerScoreTeamFilter}
                      onChange={(e) => setSelectedPlayerScoreTeamFilter(e.target.value === '' ? null : Number(e.target.value))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">All Teams</option>
                      {teams.map((team: any) => (
                        <option key={team.teamId} value={team.teamId}>
                          {team.teamName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Player Count and Full Page Button */}
                  <div className="flex gap-2 items-center">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-300">
                      Total Players: {flattenedPlayerScores.length}
                    </span>
                    {selectedPlayerScoreTeamFilter !== null && (
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-300">
                        Viewing: {teams.find((t: any) => t.teamId === selectedPlayerScoreTeamFilter)?.teamName || 'Unknown Team'}
                      </span>
                    )}
                    <button
                      onClick={() => navigate(`/dashboard/series/${seriesId}/player-scores`)}
                      className="ml-2 inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      <FaExternalLinkAlt className="h-3.5 w-3.5 mr-1.5" />
                      View Full Page
                    </button>
                  </div>
                </div>
              )}

              {flattenedPlayerScores.length > 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="ag-theme-material" style={{ height: 'calc(100vh - 400px)', minHeight: '400px', width: '100%' }}>
                    <AgGridReact
                      rowData={flattenedPlayerScores}
                      columnDefs={playerScoresColumns}
                      defaultColDef={{ sortable: true, resizable: true }}
                      pagination={true}
                      paginationPageSize={25}
                      paginationPageSizeSelector={[10, 25, 50, 100]}
                      rowHeight={50}
                      theme={themeQuartz}
                      getRowId={(params: any) => params.data._id || params.data.playerId || `player-${params.data.playerName}`}
                      onRowClicked={handlePlayerScoreRowClick}
                      rowClass="cursor-pointer hover:bg-blue-50"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center">
                  <p className="text-sm text-gray-500">
                    {playerScores.length === 0 ? 'No player scores available' : 'No players found for the selected team'}
                  </p>
                </div>
              )}
            </>
          )}
        </TabPanel>

        {/* Plan Config Tab */}
        <TabPanel value={tabValue} index={6}>
          <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-6">
            {/* Free Series Toggle Banner */}
            <div className={`mb-6 flex items-center justify-between p-4 rounded-xl border ${
              editIsFree
                ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20'
                : 'bg-gray-50 dark:bg-dark-300/30 border-gray-200 dark:border-dark-300/40'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  editIsFree ? 'bg-emerald-100 dark:bg-emerald-500/20' : 'bg-gray-100 dark:bg-dark-300/40'
                }`}>
                  <svg className={`h-5 w-5 ${editIsFree ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className={`text-sm font-semibold ${editIsFree ? 'text-emerald-800 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-300'}`}>
                    Free Series
                  </p>
                  <p className={`text-xs ${editIsFree ? 'text-emerald-600 dark:text-emerald-400/70' : 'text-gray-500 dark:text-gray-400'}`}>
                    {series?.publish
                      ? (editIsFree ? 'Free series — locked while published.' : 'Locked while published.')
                      : editIsFree
                        ? 'All available features are unlocked. No plan selection or credits required.'
                        : 'Enable to skip plan selection and unlock all available features for free.'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleIsFree}
                disabled={savingIsFree || series?.publish}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  editIsFree ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-dark-300'
                }`}
                title={series?.publish ? 'Cannot change while series is published' : ''}
              >
                <span
                  className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    editIsFree ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Plan Configuration</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={selectedFeatureTemplateId}
                  onChange={(e) => loadFeatureTemplate(e.target.value)}
                  disabled={loadingFeatures || loadingTemplatesList}
                  className="px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                >
                  <option value="">
                    {loadingTemplatesList
                      ? 'Loading templates...'
                      : featureTemplates.length === 0
                        ? 'No templates available'
                        : `Select a template (${featureTemplates.length})`}
                  </option>
                  {featureTemplates.map((template) => {
                    const templateId = template._id || template.id || '';
                    return (
                      <option key={templateId} value={templateId}>
                        {template.name}
                      </option>
                    );
                  })}
                </select>
                <button
                  onClick={saveSeriesFeatures}
                  disabled={savingFeatures || seriesFeatures.featureCategories.length === 0 || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
                  title={series?.publish ? 'Cannot modify published series' : 'Save to Series'}
                >
                  {savingFeatures ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                  ) : (
                    <FaList className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {savingFeatures ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={clonePlanConfigTemplate}
                  disabled={!selectedFeatureTemplateId || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors shadow-sm disabled:opacity-50"
                  title="Clone selected template"
                >
                  <FaCopy className="h-3.5 w-3.5 mr-1.5" />
                  Clone
                </button>
                <button
                  onClick={deletePlanConfigTemplate}
                  disabled={!selectedFeatureTemplateId || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50"
                  title="Delete selected template"
                >
                  <FaTrash className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </button>
                <button
                  onClick={clearPlanConfig}
                  disabled={seriesFeatures.featureCategories.length === 0 || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors shadow-sm disabled:opacity-50"
                  title="Clear current configuration"
                >
                  <FaRedo className="h-3.5 w-3.5 mr-1.5" />
                  Clear
                </button>
              </div>
            </div>

            {series?.publish && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  This series is published. Plan configuration is read-only.
                </p>
              </div>
            )}

            {loadingFeatures ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : seriesFeatures.featureCategories.length === 0 && Object.keys(seriesFeatures.tierPricing || {}).length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <FaList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{series?.publish ? 'No plan configuration set for this published series.' : 'Select a plan template to configure pricing for this series.'}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Tier Pricing Section */}
                {seriesFeatures.tierPricing && Object.keys(seriesFeatures.tierPricing).length > 0 && (
                  <div className="border border-gray-200 dark:border-dark-300/40 rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 px-4 py-3 border-b border-gray-200 dark:border-dark-300/40">
                      <h4 className="font-semibold text-gray-900 dark:text-white">Tier Pricing</h4>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {Object.entries(seriesFeatures.tierPricing).map(([tier, price]) => (
                          <div key={tier} className="bg-gray-50 dark:bg-dark-400/30 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">{tier}</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">{String(price)} <span className="text-xs font-normal text-gray-500">credits</span></p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Feature Categories */}
                {seriesFeatures.featureCategories.map((category: any) => (
                  <div key={category.key} className="border border-gray-200 dark:border-dark-300/40 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 dark:bg-dark-400/30 px-4 py-3 border-b border-gray-200 dark:border-dark-300/40">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{category.name}</h4>
                    </div>
                    <div className="p-4">
                      <table className="w-full">
                        <thead>
                          <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            <th className="text-left pb-3">Feature</th>
                            <th className="text-center pb-3 w-32">Credits</th>
                            <th className="text-center pb-3 w-40">Upgrade Policy</th>
                            <th className="text-left pb-3">Included In</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-dark-300/20">
                          {category.features.map((feature: any) => (
                            <tr key={feature.key}>
                              <td className="py-3">
                                <div className="font-medium text-gray-900 dark:text-white text-sm">{feature.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{feature.key}</div>
                              </td>
                              <td className="py-3">
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                  <input
                                    type="number"
                                    value={feature.price || 0}
                                    onChange={(e) => updateFeaturePrice(category.key, feature.key, parseFloat(e.target.value) || 0)}
                                    min="0"
                                    step="0.01"
                                    className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              </td>
                              <td className="py-3 text-center">
                                <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${
                                  feature.upgradePolicy === 'anytime'
                                    ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-400'
                                    : feature.upgradePolicy === 'before_start'
                                    ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-400'
                                    : feature.upgradePolicy === 'creation_only'
                                    ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-400'
                                    : 'bg-gray-100 dark:bg-gray-500/20 text-gray-600 dark:text-gray-400'
                                }`}>
                                  {feature.upgradePolicy === 'anytime' ? 'Anytime'
                                    : feature.upgradePolicy === 'before_start' ? 'Before Start'
                                    : feature.upgradePolicy === 'creation_only' ? 'Creation Only'
                                    : 'Not Set'}
                                </span>
                              </td>
                              <td className="py-3">
                                <div className="flex flex-wrap gap-1">
                                  {(feature.includedIn || []).map((plan: string) => (
                                    <span
                                      key={plan}
                                      className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400 rounded-full capitalize"
                                    >
                                      {plan}
                                    </span>
                                  ))}
                                  {(!feature.includedIn || feature.includedIn.length === 0) && (
                                    <span className="text-xs text-gray-400">None</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabPanel>

        {/* Rules Config Tab */}
        <TabPanel value={tabValue} index={7}>
          <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Rules Configuration</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={selectedRulesConfigTemplateId}
                  onChange={(e) => loadRulesConfigTemplate(e.target.value)}
                  disabled={loadingRulesConfig || loadingRulesConfigTemplatesList}
                  className="px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[200px]"
                >
                  <option value="">
                    {loadingRulesConfigTemplatesList
                      ? 'Loading templates...'
                      : rulesConfigTemplates.length === 0
                        ? 'No templates available'
                        : `Select a template (${rulesConfigTemplates.length})`}
                  </option>
                  {rulesConfigTemplates.map((template) => {
                    const templateId = template._id || template.id || '';
                    return (
                      <option key={templateId} value={templateId}>
                        {template.name}
                      </option>
                    );
                  })}
                </select>
                <button
                  onClick={saveSeriesRulesConfig}
                  disabled={savingRulesConfig || seriesRulesConfig.sections.length === 0 || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50"
                  title={series?.publish ? 'Cannot modify published series' : 'Save to Series'}
                >
                  {savingRulesConfig ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                  ) : (
                    <FaCog className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {savingRulesConfig ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={cloneRulesConfigTemplate}
                  disabled={!selectedRulesConfigTemplateId || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors shadow-sm disabled:opacity-50"
                  title="Clone selected template"
                >
                  <FaCopy className="h-3.5 w-3.5 mr-1.5" />
                  Clone
                </button>
                <button
                  onClick={deleteRulesConfigTemplate}
                  disabled={!selectedRulesConfigTemplateId || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50"
                  title="Delete selected template"
                >
                  <FaTrash className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </button>
                <button
                  onClick={clearRulesConfig}
                  disabled={seriesRulesConfig.sections.length === 0 || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors shadow-sm disabled:opacity-50"
                  title="Clear current configuration"
                >
                  <FaRedo className="h-3.5 w-3.5 mr-1.5" />
                  Clear
                </button>
              </div>
            </div>

            {series?.publish && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  This series is published. Rules configuration is read-only.
                </p>
              </div>
            )}

            {loadingRulesConfig ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : seriesRulesConfig.sections.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <FaCog className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{series?.publish ? 'No rules configuration set for this published series.' : 'Select a rules template to configure for this series.'}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {seriesRulesConfig.sections.map((section: any) => (
                  <div key={section.section} className="border border-gray-200 dark:border-dark-300/40 rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 px-4 py-3 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{section.section}</h4>
                        {section.dependsOn && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Depends on: {section.dependsOn.key} = {String(section.dependsOn.value)}
                          </p>
                        )}
                      </div>
                      {section.locked && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">Locked</span>
                      )}
                    </div>
                    <div className="p-4">
                      {/* Section Notes */}
                      {section.notes && section.notes.length > 0 && (
                        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                          <ul className="text-sm text-amber-800 dark:text-amber-300 list-disc list-inside">
                            {section.notes.map((note: string, idx: number) => (
                              <li key={idx}>{note}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {/* Fields Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {section.fields.map((field: any) => (
                          <div key={field.key} className={`bg-gray-50 dark:bg-dark-400/30 rounded-lg p-3 ${field.available === false ? 'opacity-40' : ''} ${field.locked ? 'border-l-2 border-l-red-400' : ''} ${field.frozen ? 'border-r-2 border-r-cyan-400' : ''}`}>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500 dark:text-gray-400">{field.label}</p>
                              <div className="flex gap-1">
                                {field.frozen && <span className="text-xs text-cyan-500" title="Frozen after publish">❄</span>}
                                {!field.active && <span className="text-xs text-gray-400" title="Inactive">○</span>}
                              </div>
                            </div>
                            {field.desc && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 italic">{field.desc}</p>
                            )}
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {field.type === 'boolean'
                                ? ((field.value ?? field.default) ? 'Yes' : 'No')
                                : field.type === 'minMaxRange'
                                  ? `${(field.value || field.default)?.min ?? 0} - ${(field.value || field.default)?.max ?? 0}`
                                  : field.type === 'select'
                                    ? (() => {
                                        const val = field.value ?? field.default;
                                        if (typeof val === 'object' && val !== null) return JSON.stringify(val);
                                        return field.options?.find((o: any) => o.value === val)?.label || String(val ?? '—');
                                      })()
                                    : field.type === 'datetime'
                                      ? (field.value ? new Date(field.value as string).toLocaleString() : 'Not set')
                                      : field.type === 'transferWindows'
                                        ? `${Array.isArray(field.value) ? (field.value as any[]).length : 0} windows`
                                        : (() => {
                                            const val = field.value ?? field.default;
                                            if (val === null || val === undefined) return '—';
                                            if (typeof val === 'object') return JSON.stringify(val);
                                            return String(val);
                                          })()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabPanel>

        {/* Scoring Rules Tab */}
        <TabPanel value={tabValue} index={8}>
          <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Scoring Rules Configuration</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={selectedScoringRulesTemplateId}
                  onChange={(e) => loadScoringRulesTemplate(e.target.value)}
                  disabled={loadingScoringRules || loadingScoringRulesTemplatesList}
                  className="px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[200px]"
                >
                  <option value="">
                    {loadingScoringRulesTemplatesList
                      ? 'Loading templates...'
                      : scoringRulesTemplates.length === 0
                        ? 'No templates available'
                        : `Select a template (${scoringRulesTemplates.length})`}
                  </option>
                  {scoringRulesTemplates.map((template) => {
                    const templateId = template._id || template.id || '';
                    return (
                      <option key={templateId} value={templateId}>
                        {template.name}
                      </option>
                    );
                  })}
                </select>
                <button
                  onClick={saveSeriesScoringRules}
                  disabled={savingScoringRules || seriesScoringRules.sections.length === 0 || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50"
                  title={series?.publish ? 'Cannot modify published series' : 'Save to Series'}
                >
                  {savingScoringRules ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                  ) : (
                    <FaChartBar className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {savingScoringRules ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={cloneScoringRulesTemplate}
                  disabled={!selectedScoringRulesTemplateId || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors shadow-sm disabled:opacity-50"
                  title="Clone selected template"
                >
                  <FaCopy className="h-3.5 w-3.5 mr-1.5" />
                  Clone
                </button>
                <button
                  onClick={deleteScoringRulesTemplate}
                  disabled={!selectedScoringRulesTemplateId || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50"
                  title="Delete selected template"
                >
                  <FaTrash className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </button>
                <button
                  onClick={clearScoringRules}
                  disabled={seriesScoringRules.sections.length === 0 || series?.publish}
                  className="inline-flex items-center px-3 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors shadow-sm disabled:opacity-50"
                  title="Clear current configuration"
                >
                  <FaRedo className="h-3.5 w-3.5 mr-1.5" />
                  Clear
                </button>
              </div>
            </div>

            {series?.publish && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  This series is published. Scoring rules configuration is read-only.
                </p>
              </div>
            )}

            {loadingScoringRules ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : seriesScoringRules.sections.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <FaChartBar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{series?.publish ? 'No scoring rules configuration set for this published series.' : 'Select a scoring rules template to configure for this series.'}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {seriesScoringRules.sections.map((section: any) => (
                  <div key={section.section} className="border border-gray-200 dark:border-dark-300/40 rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 px-4 py-3 border-b border-gray-200 dark:border-dark-300/40">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{section.section}</h4>
                    </div>
                    <div className="p-4">
                      {/* Section Notes */}
                      {section.notes && section.notes.length > 0 && (
                        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                          <ul className="text-sm text-amber-800 dark:text-amber-300 list-disc list-inside">
                            {section.notes.map((note: string, idx: number) => (
                              <li key={idx}>{note}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {/* Fields Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {section.fields.map((field: any) => (
                          <div key={field.key} className="bg-gray-50 dark:bg-dark-400/30 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400">{field.label}</p>
                              {field.operation && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  field.operation === 'multiplier'
                                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                    : field.operation === 'substract'
                                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                }`}>
                                  {field.operation === 'multiplier' ? '×' : field.operation === 'substract' ? '−' : '+'}
                                </span>
                              )}
                            </div>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {field.type === 'boolean'
                                ? ((field.value ?? field.default) ? 'Yes' : 'No')
                                : (() => {
                                    const val = field.value ?? field.default;
                                    if (val === null || val === undefined) return '—';
                                    if (typeof val === 'object') return JSON.stringify(val);
                                    return String(val);
                                  })()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabPanel>
      </div>

      {/* Contest Details Modal with Tabs */}
      {contestDetailsModalOpen && selectedContest && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 text-white">
                  <FaInfoCircle className="h-6 w-6" />
                  <div>
                    <h3 className="text-lg font-semibold">Contest Details</h3>
                    <p className="text-sm text-blue-100">
                      {selectedContest.name || selectedContest.contestName || `Contest ${selectedContest.contestId || selectedContest.id}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setContestDetailsModalOpen(false)}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors text-white"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <nav className="flex space-x-4 px-6" aria-label="Tabs">
                {[
                  { name: 'Contest Info', icon: FaInfoCircle },
                  { name: 'Rules', icon: FaList },
                  { name: 'Scoring', icon: FaChartBar },
                  { name: 'Owner', icon: FaUser },
                  { name: 'Members', icon: FaUsers },
                  { name: 'Leaderboard', icon: FaTrophy }
                ].map((tab, index) => (
                  <button
                    key={tab.name}
                    onClick={() => setContestDetailsTab(index)}
                    className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      contestDetailsTab === index
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="mr-2" />
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {/* Contest Info Tab */}
              {contestDetailsTab === 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contest Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(selectedContest)
                      .filter(([key]) => !['rules', 'scoringSystem', 'scoring_system', 'owner', 'ownerInfo', 'owner_info'].includes(key))
                      .map(([key, value]) => (
                        <div key={key} className="bg-gray-50 p-4 rounded-lg">
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                            {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                          </div>
                          <div className="text-sm text-gray-900">
                            {value === null || value === undefined ? (
                              <span className="text-gray-400 italic">—</span>
                            ) : typeof value === 'boolean' ? (
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                                value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {value ? 'Yes' : 'No'}
                              </span>
                            ) : typeof value === 'object' ? (
                              Array.isArray(value) ? (
                                value.length === 0 ? (
                                  <span className="text-gray-400 italic">Empty</span>
                                ) : (
                                  <div className="space-y-1">
                                    {value.map((item, idx) => (
                                      <div key={idx} className="bg-white p-2 rounded border border-gray-200 text-xs">
                                        {typeof item === 'object' ? (
                                          <div className="space-y-0.5">
                                            {Object.entries(item).map(([k, v]) => (
                                              <div key={k} className="flex">
                                                <span className="font-semibold text-gray-600 min-w-[80px]">{k}:</span>
                                                <span className="text-gray-900">{String(v)}</span>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          String(item)
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )
                              ) : (
                                <div className="bg-white p-2 rounded border border-gray-200">
                                  <div className="space-y-0.5 text-xs">
                                    {Object.entries(value).map(([k, v]) => (
                                      <div key={k} className="flex">
                                        <span className="font-semibold text-gray-600 min-w-[100px]">{k}:</span>
                                        <span className="text-gray-900">{String(v)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            ) : key.toLowerCase().includes('date') || key.toLowerCase().includes('time') ? (
                              (() => {
                                try {
                                  return new Date(value as string).toLocaleString();
                                } catch {
                                  return String(value);
                                }
                              })()
                            ) : (
                              String(value)
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Rules Tab */}
              {contestDetailsTab === 1 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contest Rules</h3>
                  {selectedContest.rules ? (
                    (() => {
                      let rulesData = selectedContest.rules;

                      // Parse JSON string if needed
                      if (typeof rulesData === 'string') {
                        try {
                          rulesData = JSON.parse(rulesData);
                        } catch (e) {
                          return (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <pre className="whitespace-pre-wrap text-sm">{rulesData}</pre>
                            </div>
                          );
                        }
                      }

                      // Only show raw JSON for non-object data
                      if (typeof rulesData !== 'object' || rulesData === null) {
                        return (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <pre className="whitespace-pre-wrap text-sm">
                              {JSON.stringify(rulesData, null, 2)}
                            </pre>
                          </div>
                        );
                      }

                      // Handle arrays by converting to object format
                      let dataToProcess = rulesData;
                      if (Array.isArray(rulesData)) {
                        dataToProcess = rulesData.reduce((acc, item, index) => {
                          if (typeof item === 'object' && item !== null) {
                            return { ...acc, ...item };
                          } else {
                            acc[`Item ${index + 1}`] = item;
                            return acc;
                          }
                        }, {});
                      }

                      // Group rules by category (nested objects become groups)
                      const groups: { title: string; rules: [string, any][] }[] = [];
                      const ungroupedRules: [string, any][] = [];

                      Object.entries(dataToProcess).forEach(([key, value]) => {
                        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                          // This is a group
                          groups.push({
                            title: key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim(),
                            rules: Object.entries(value)
                          });
                        } else {
                          // This is a standalone rule
                          ungroupedRules.push([key, value]);
                        }
                      });

                      return (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-2/3">
                                  Rule
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/3">
                                  Value
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {/* Ungrouped rules first */}
                              {ungroupedRules.map(([key, value]) => (
                                <tr key={key} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 text-sm text-gray-900">
                                    {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                  </td>
                                  <td className="px-6 py-4 text-sm">
                                    {value === null || value === undefined ? (
                                      <span className="text-gray-400 italic">—</span>
                                    ) : typeof value === 'boolean' ? (
                                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                                        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                      }`}>
                                        {value ? 'Yes' : 'No'}
                                      </span>
                                    ) : typeof value === 'number' ? (
                                      <span className="font-semibold text-blue-600">{value}</span>
                                    ) : (
                                      <span className="text-gray-900">{String(value)}</span>
                                    )}
                                  </td>
                                </tr>
                              ))}

                              {/* Grouped rules with category headers */}
                              {groups.map((group, groupIdx) => (
                                <React.Fragment key={groupIdx}>
                                  {/* Group Header */}
                                  <tr className="bg-purple-50 border-t-2 border-purple-200">
                                    <td colSpan={2} className="px-6 py-3">
                                      <h4 className="text-sm font-bold text-purple-900 uppercase tracking-wide">
                                        {group.title}
                                      </h4>
                                    </td>
                                  </tr>
                                  {/* Group Rules */}
                                  {group.rules.map(([key, value]) => (
                                    <tr key={key} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 text-sm text-gray-900 pl-12">
                                        {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                      </td>
                                      <td className="px-6 py-4 text-sm">
                                        {value === null || value === undefined ? (
                                          <span className="text-gray-400 italic">—</span>
                                        ) : typeof value === 'boolean' ? (
                                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                                            value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                          }`}>
                                            {value ? 'Yes' : 'No'}
                                          </span>
                                        ) : typeof value === 'number' ? (
                                          <span className="font-semibold text-blue-600">{value}</span>
                                        ) : (
                                          <span className="text-gray-900">{String(value)}</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()
                  ) : (
                    <p className="text-gray-500 italic">No rules available</p>
                  )}
                </div>
              )}

              {/* Scoring Tab */}
              {contestDetailsTab === 2 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Scoring System</h3>
                  {selectedContest.scoringSystem || selectedContest.scoring_system ? (
                    (() => {
                      let scoringData = selectedContest.scoringSystem || selectedContest.scoring_system;

                      // Parse JSON string if needed
                      if (typeof scoringData === 'string') {
                        try {
                          scoringData = JSON.parse(scoringData);
                        } catch (e) {
                          return (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <pre className="whitespace-pre-wrap text-sm">{scoringData}</pre>
                            </div>
                          );
                        }
                      }

                      // Only show raw JSON for non-object data
                      if (typeof scoringData !== 'object' || scoringData === null) {
                        return (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <pre className="whitespace-pre-wrap text-sm">
                              {JSON.stringify(scoringData, null, 2)}
                            </pre>
                          </div>
                        );
                      }

                      // Handle array structure with sections
                      const sections = Array.isArray(scoringData) ? scoringData : [scoringData];

                      return (
                        <div className="space-y-3">
                          {sections.map((sectionItem: any, sectionIndex: number) => {
                            const sectionTitle = sectionItem.section || `Section ${sectionIndex + 1}`;
                            const fields = sectionItem.fields || [];
                            const notes = sectionItem.notes || [];

                            return (
                              <div key={sectionItem._id || sectionIndex} className="bg-white border border-gray-200 rounded-md overflow-hidden shadow-sm">
                                {/* Section Header */}
                                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-3 py-2">
                                  <h4 className="text-sm font-bold text-white uppercase tracking-wide">
                                    {sectionTitle}
                                  </h4>
                                </div>

                                {/* Section Sub-Table */}
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-2/3">
                                        Field
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/3">
                                        Default Value
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {fields.length > 0 ? (
                                      fields.map((field: any, fieldIndex: number) => (
                                        <tr key={field._id || fieldIndex} className="hover:bg-gray-50 transition-colors">
                                          <td className="px-3 py-2 text-xs text-gray-900 font-medium">
                                            {field.label || field.key || 'Unknown'}
                                          </td>
                                          <td className="px-3 py-2 text-xs">
                                            {field.type === 'boolean' ? (
                                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                                                field.default ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                              }`}>
                                                {field.default ? '✓' : '✗'}
                                              </span>
                                            ) : typeof field.default === 'number' ? (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                                                {field.default}
                                              </span>
                                            ) : (
                                              <span className="text-gray-900">{String(field.default)}</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={2} className="px-3 py-2 text-xs text-gray-500 italic text-center">
                                          No fields available
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>

                                {/* Notes Section */}
                                {notes.length > 0 && (
                                  <div className="bg-yellow-50 border-t border-yellow-200 px-3 py-2">
                                    <div className="flex items-start space-x-2">
                                      <span className="text-yellow-600 font-semibold text-xs uppercase">Notes:</span>
                                      <ul className="list-disc list-inside text-xs text-yellow-800 space-y-0.5">
                                        {notes.map((note: string, noteIndex: number) => (
                                          <li key={noteIndex}>{note}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="text-gray-500 italic">No scoring system available</p>
                  )}
                </div>
              )}

              {/* Owner Tab */}
              {contestDetailsTab === 3 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Owner Information</h3>
                  {selectedContest.owner || selectedContest.ownerInfo || selectedContest.owner_info ? (
                    (() => {
                      let ownerData = selectedContest.owner || selectedContest.ownerInfo || selectedContest.owner_info;

                      // Parse JSON string if needed
                      if (typeof ownerData === 'string') {
                        try {
                          ownerData = JSON.parse(ownerData);
                        } catch (e) {
                          return (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <pre className="whitespace-pre-wrap text-sm">{ownerData}</pre>
                            </div>
                          );
                        }
                      }

                      // Only show raw JSON for non-object data
                      if (typeof ownerData !== 'object' || ownerData === null) {
                        return (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <pre className="whitespace-pre-wrap text-sm">
                              {JSON.stringify(ownerData, null, 2)}
                            </pre>
                          </div>
                        );
                      }

                      // Handle arrays by converting to object format
                      let dataToProcess = ownerData;
                      if (Array.isArray(ownerData)) {
                        dataToProcess = ownerData.reduce((acc, item, index) => {
                          if (typeof item === 'object' && item !== null) {
                            return { ...acc, ...item };
                          } else {
                            acc[`Item ${index + 1}`] = item;
                            return acc;
                          }
                        }, {});
                      }

                      // Group owner data by category (nested objects become groups)
                      const groups: { title: string; rules: [string, any][] }[] = [];
                      const ungroupedData: [string, any][] = [];

                      Object.entries(dataToProcess).forEach(([key, value]) => {
                        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                          // This is a group
                          groups.push({
                            title: key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim(),
                            rules: Object.entries(value)
                          });
                        } else {
                          // This is a standalone field
                          ungroupedData.push([key, value]);
                        }
                      });

                      return (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/3">
                                  Field
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-2/3">
                                  Value
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {/* Ungrouped data first */}
                              {ungroupedData.map(([key, value]) => (
                                <tr key={key} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 text-sm font-medium text-gray-700">
                                    {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                  </td>
                                  <td className="px-6 py-4 text-sm">
                                    {value === null || value === undefined ? (
                                      <span className="text-gray-400 italic">—</span>
                                    ) : typeof value === 'boolean' ? (
                                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                                        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                      }`}>
                                        {value ? 'Yes' : 'No'}
                                      </span>
                                    ) : key.toLowerCase().includes('id') ? (
                                      <span className="font-mono text-xs text-gray-900">{String(value)}</span>
                                    ) : key.toLowerCase().includes('email') ? (
                                      <a href={`mailto:${value}`} className="text-blue-600 hover:underline">
                                        {String(value)}
                                      </a>
                                    ) : (
                                      <span className="text-gray-900">{String(value)}</span>
                                    )}
                                  </td>
                                </tr>
                              ))}

                              {/* Grouped data with category headers */}
                              {groups.map((group, groupIdx) => (
                                <React.Fragment key={groupIdx}>
                                  {/* Group Header */}
                                  <tr className="bg-green-50 border-t-2 border-green-200">
                                    <td colSpan={2} className="px-6 py-3">
                                      <h4 className="text-sm font-bold text-green-900 uppercase tracking-wide">
                                        {group.title}
                                      </h4>
                                    </td>
                                  </tr>
                                  {/* Group Data */}
                                  {group.rules.map(([key, value]) => (
                                    <tr key={key} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 text-sm font-medium text-gray-700 pl-12">
                                        {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                      </td>
                                      <td className="px-6 py-4 text-sm">
                                        {value === null || value === undefined ? (
                                          <span className="text-gray-400 italic">—</span>
                                        ) : typeof value === 'boolean' ? (
                                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                                            value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                          }`}>
                                            {value ? 'Yes' : 'No'}
                                          </span>
                                        ) : key.toLowerCase().includes('id') ? (
                                          <span className="font-mono text-xs text-gray-900">{String(value)}</span>
                                        ) : key.toLowerCase().includes('email') ? (
                                          <a href={`mailto:${value}`} className="text-blue-600 hover:underline">
                                            {String(value)}
                                          </a>
                                        ) : (
                                          <span className="text-gray-900">{String(value)}</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()
                  ) : (
                    <p className="text-gray-500 italic">No owner information available</p>
                  )}
                </div>
              )}

              {/* Members Tab */}
              {contestDetailsTab === 4 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contest Members</h3>
                  {loadingMembers ? (
                    <div className="flex flex-col items-center justify-center py-12 space-y-3">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      <p className="text-gray-600">Loading members...</p>
                    </div>
                  ) : contestMembers && contestMembers.length > 0 ? (
                    <div className="ag-theme-material" style={{ height: '500px', width: '100%' }}>
                      <AgGridReact
                        rowData={contestMembers}
                        columnDefs={[
                          {
                            field: 'userId',
                            headerName: 'User ID',
                            flex: 1.5,
                            filter: 'agTextColumnFilter',
                            cellRenderer: (params: ICellRendererParams) => (
                              <div className="font-mono text-xs">{params.value || 'N/A'}</div>
                            ),
                          },
                          {
                            field: 'userName',
                            headerName: 'User Name',
                            flex: 1.5,
                            filter: 'agTextColumnFilter',
                          },
                          {
                            field: 'email',
                            headerName: 'Email',
                            flex: 2,
                            filter: 'agTextColumnFilter',
                          },
                          {
                            field: 'teamName',
                            headerName: 'Team Name',
                            flex: 1.5,
                            filter: 'agTextColumnFilter',
                          },
                          {
                            field: 'joinedAt',
                            headerName: 'Joined At',
                            flex: 1.5,
                            filter: 'agDateColumnFilter',
                            valueFormatter: (params) => {
                              if (!params.value) return 'N/A';
                              try {
                                return new Date(params.value).toLocaleString();
                              } catch {
                                return params.value;
                              }
                            }
                          }
                        ]}
                        defaultColDef={{ sortable: true, resizable: true, filter: true }}
                        pagination
                        paginationPageSize={10}
                        theme={themeQuartz}
                        getRowId={(params: any) => params.data?.userId || params.data?.id || String(Math.random())}
                      />
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No members found</p>
                  )}
                </div>
              )}

              {/* Leaderboard Tab */}
              {contestDetailsTab === 5 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contest Leaderboard</h3>
                  {loadingLeaderboard ? (
                    <div className="flex flex-col items-center justify-center py-12 space-y-3">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
                      <p className="text-gray-600">Loading leaderboard...</p>
                    </div>
                  ) : contestLeaderboard && contestLeaderboard.length > 0 ? (
                    <div className="ag-theme-material" style={{ height: '500px', width: '100%' }}>
                      <AgGridReact
                        rowData={contestLeaderboard}
                        columnDefs={[
                          {
                            field: 'rank',
                            headerName: 'Rank',
                            width: 100,
                            filter: 'agNumberColumnFilter',
                            cellRenderer: (params: ICellRendererParams) => {
                              const rank = params.value;
                              let badgeColor = 'bg-gray-100 text-gray-800 border-gray-300';
                              if (rank === 1) badgeColor = 'bg-yellow-100 text-yellow-800 border-yellow-300';
                              else if (rank === 2) badgeColor = 'bg-gray-200 text-gray-800 border-gray-400';
                              else if (rank === 3) badgeColor = 'bg-orange-100 text-orange-800 border-orange-300';

                              return (
                                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${badgeColor}`}>
                                  #{rank}
                                </span>
                              );
                            }
                          },
                          {
                            field: 'userId',
                            headerName: 'User ID',
                            flex: 1.5,
                            filter: 'agTextColumnFilter',
                            cellRenderer: (params: ICellRendererParams) => (
                              <div className="font-mono text-xs">{params.value || 'N/A'}</div>
                            ),
                          },
                          {
                            field: 'userName',
                            headerName: 'User Name',
                            flex: 1.5,
                            filter: 'agTextColumnFilter',
                          },
                          {
                            field: 'teamName',
                            headerName: 'Team Name',
                            flex: 1.5,
                            filter: 'agTextColumnFilter',
                          },
                          {
                            field: 'totalPoints',
                            headerName: 'Total Points',
                            flex: 1,
                            filter: 'agNumberColumnFilter',
                            cellRenderer: (params: ICellRendererParams) => (
                              <span className="font-semibold text-green-700">{params.value || 0}</span>
                            ),
                          },
                          {
                            field: 'winnings',
                            headerName: 'Winnings',
                            flex: 1,
                            filter: 'agNumberColumnFilter',
                            cellRenderer: (params: ICellRendererParams) => (
                              <span className="font-semibold text-blue-700">{params.value ? `$${params.value}` : '-'}</span>
                            ),
                          }
                        ]}
                        defaultColDef={{ sortable: true, resizable: true, filter: true }}
                        pagination
                        paginationPageSize={10}
                        theme={themeQuartz}
                        getRowId={(params: any) => params.data?.userId || params.data?.id || String(Math.random())}
                      />
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No leaderboard data found</p>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                onClick={() => setContestDetailsModalOpen(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Player Score Details Modal */}
      {playerScoreDetailsModalOpen && selectedPlayerScore && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-400/95 backdrop-blur-sm rounded-lg dark:rounded-xl shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-primary-600 dark:to-primary-700 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 text-white">
                  <FaUser className="h-6 w-6" />
                  <div>
                    <h3 className="text-lg font-semibold">{selectedPlayerScore.playerName}</h3>
                    <p className="text-sm text-blue-100 dark:text-primary-100">Match by Match Breakdown</p>
                  </div>
                </div>
                <button
                  onClick={() => setPlayerScoreDetailsModalOpen(false)}
                  className="text-white hover:text-blue-100 dark:hover:text-primary-100 transition-colors"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedPlayerScore.matches && selectedPlayerScore.matches.length > 0 ? (
                <div className="space-y-4">
                  {selectedPlayerScore.matches.map((match: any, index: number) => {
                    const breakdown = match.scoresBreakdown || {};
                    // Find the match details from matches
                    const matchDetails = matches?.find((m: any) => m.matchId === match.matchId);
                    const localTeamName = matchDetails?.localTeam?.name || matchDetails?.localTeam?.teamName || matchDetails?.localTeam || 'Team A';
                    const visitorTeamName = matchDetails?.visitorTeam?.name || matchDetails?.visitorTeam?.teamName || matchDetails?.visitorTeam || 'Team B';

                    return (
                      <div
                        key={index}
                        className="bg-white dark:bg-dark-300/40 border border-gray-200 dark:border-dark-300/40 rounded-lg dark:rounded-xl p-4 shadow-sm dark:shadow-lg hover:shadow-md dark:hover:shadow-xl transition-shadow"
                      >
                        <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-dark-300/40">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900 dark:text-white">Match #{index + 1}</h4>
                              <span className="text-xs text-gray-500 dark:text-gray-400">ID: {match.matchId}</span>
                            </div>
                            <p className="text-sm font-medium text-blue-600 dark:text-primary-400">
                              {localTeamName} vs {visitorTeamName}
                            </p>
                          </div>
                          {breakdown.mom ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-500/30">
                              <FaTrophy className="h-3 w-3 mr-1" /> Man of the Match
                            </span>
                          ) : null}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {/* Batting Stats */}
                          <div className="bg-blue-50 dark:bg-primary-500/10 p-3 rounded-lg dark:rounded-xl">
                            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Batting</div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Runs:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.runsScored || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Balls:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.ballsFaced || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">4s/6s:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.fours || 0}/{breakdown.sixes || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">SR:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {breakdown.ballsFaced > 0 ? ((breakdown.runsScored / breakdown.ballsFaced) * 100).toFixed(2) : '0.00'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Bowling Stats */}
                          <div className="bg-green-50 dark:bg-success-500/10 p-3 rounded-lg dark:rounded-xl">
                            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Bowling</div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Wickets:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.wickets || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Overs:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.overs || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Runs:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.runsConceded || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Econ:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {breakdown.overs > 0 ? (breakdown.runsConceded / breakdown.overs).toFixed(2) : '-'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Fielding Stats */}
                          <div className="bg-purple-50 dark:bg-purple-500/10 p-3 rounded-lg dark:rounded-xl">
                            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Fielding</div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Catches:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.catch || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Stumps:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.stump || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Runouts:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.runouts || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto h-16 w-16 bg-gray-100 dark:bg-dark-300/40 rounded-full flex items-center justify-center mb-4">
                    <FaChartBar className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Match Data</h3>
                  <p className="text-gray-600 dark:text-gray-400">No match-by-match breakdown available for this player</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-dark-300/40 border-t border-gray-200 dark:border-dark-300/40 flex justify-end flex-shrink-0">
              <button
                onClick={() => setPlayerScoreDetailsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-dark-300/60 text-gray-700 dark:text-gray-300 rounded-lg dark:rounded-xl hover:bg-gray-300 dark:hover:bg-dark-300/80 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish/Unpublish Confirmation Dialog */}
      {showPublishDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-fadeIn">
            {/* Dialog Header */}
            <div className={`px-6 py-4 ${
              publishAction === 'publish'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                : 'bg-gradient-to-r from-orange-500 to-red-600'
            }`}>
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {publishAction === 'publish' ? (
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                </div>
                <h3 className="text-lg font-bold text-white">
                  {publishAction === 'publish' ? 'Publish Series' : 'Unpublish Series'}
                </h3>
              </div>
            </div>

            {/* Dialog Content */}
            <div className="px-6 py-5">
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                {publishAction === 'publish' ? (
                  <>
                    Are you sure you want to <strong className="text-green-600">publish</strong> this series?
                  </>
                ) : (
                  <>
                    Are you sure you want to <strong className="text-orange-600">unpublish</strong> this series?
                  </>
                )}
              </p>
              <div className={`border-l-4 p-4 rounded ${
                publishAction === 'publish'
                  ? 'bg-blue-50 border-blue-400'
                  : 'bg-yellow-50 border-yellow-400'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className={`h-5 w-5 ${
                      publishAction === 'publish' ? 'text-blue-400' : 'text-yellow-400'
                    }`} viewBox="0 0 20 20" fill="currentColor">
                      {publishAction === 'publish' ? (
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      )}
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm ${
                      publishAction === 'publish' ? 'text-blue-700' : 'text-yellow-700'
                    }`}>
                      {publishAction === 'publish' ? (
                        <>
                          This will make the series <strong>visible to all users</strong> and enable contest participation.
                        </>
                      ) : (
                        <>
                          This will make the series <strong>unavailable to users</strong> and may affect ongoing contests.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Dialog Actions */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowPublishDialog(false)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmPublishToggle}
                disabled={publishing}
                className={`px-4 py-2 text-white rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                  publishAction === 'publish'
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:ring-green-500'
                    : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 focus:ring-orange-500'
                }`}
              >
                {publishing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {publishAction === 'publish' ? 'Publishing...' : 'Unpublishing...'}
                  </>
                ) : (
                  publishAction === 'publish' ? 'Yes, Publish' : 'Yes, Unpublish'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Series Information Dialog */}
      {showEditSeriesDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-400 rounded-lg shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-fadeIn">
            {/* Dialog Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 dark:from-primary-600 dark:to-primary-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Edit Series Information</h3>
                <button
                  onClick={() => setShowEditSeriesDialog(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Dialog Content */}
            <div className="px-6 py-5 space-y-5">
              {/* Alternate Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Alternate Name
                </label>
                <input
                  type="text"
                  value={editAlternateName}
                  onChange={(e) => setEditAlternateName(e.target.value)}
                  placeholder="Enter alternate name"
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  An alternative display name for the series
                </p>
              </div>

              {/* Short Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Short Name
                </label>
                <input
                  type="text"
                  value={editShortName}
                  onChange={(e) => setEditShortName(e.target.value)}
                  placeholder="Enter short name"
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  A short abbreviated name for the series
                </p>
              </div>

              {/* Host Country (for league series) */}
              {(series as any)?.type === 'league' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Host Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editCountry}
                    onChange={(e) => setEditCountry(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select host country</option>
                    <option value="Afghanistan">Afghanistan</option>
                    <option value="Australia">Australia</option>
                    <option value="Bangladesh">Bangladesh</option>
                    <option value="England">England</option>
                    <option value="India">India</option>
                    <option value="Ireland">Ireland</option>
                    <option value="Nepal">Nepal</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="New Zealand">New Zealand</option>
                    <option value="Pakistan">Pakistan</option>
                    <option value="Scotland">Scotland</option>
                    <option value="South Africa">South Africa</option>
                    <option value="Sri Lanka">Sri Lanka</option>
                    <option value="United Arab Emirates">United Arab Emirates</option>
                    <option value="United States">United States</option>
                    <option value="West Indies">West Indies</option>
                    <option value="Zimbabwe">Zimbabwe</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    The home country of this league. Players from other countries will be marked as overseas. Required for publishing.
                  </p>
                </div>
              )}

              {/* Toggles Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Trending Toggle */}
                <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Trending
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={handleToggleTrending}
                      disabled={savingTrending}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        editTrending ? 'bg-blue-600 dark:bg-primary-600' : 'bg-gray-200 dark:bg-dark-300'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          editTrending ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                      {savingTrending ? 'Updating...' : editTrending ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                {/* Active Toggle */}
                <div className="bg-gray-50 dark:bg-dark-300/40 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Active
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={handleToggleActive}
                      disabled={savingActive}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        editActive ? 'bg-green-600 dark:bg-success-600' : 'bg-gray-200 dark:bg-dark-300'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          editActive ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                      {savingActive ? 'Updating...' : editActive ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dialog Actions */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-dark-400/80 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditSeriesDialog(false)}
                className="px-4 py-2 bg-white dark:bg-dark-300 border border-gray-300 dark:border-dark-300/40 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-400/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleSaveSeriesInfo();
                  setShowEditSeriesDialog(false);
                }}
                disabled={savingSeriesInfo}
                className="px-4 py-2 bg-blue-600 dark:bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {savingSeriesInfo ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default SeriesDetailsMUI;
